// services/chatStream/chatStreamClient.ts
import {
  CHAT_TURN_ROUTE,
  isTerminalChatStreamEvent,
  type ChatStreamEvent,
  type ChatTurnRequest,
} from '../../types/chatStream';
import type { ClientUpdateRequiredPayload } from '../../types/clientUpdate';
import { TWO_FACTOR_SETUP_REQUIRED_CODE } from '../authRetryExemptions';
import { getClientMetadataHeaders } from '../clientVersion';
import { parseClientUpdateRequiredPayload } from '../clientUpdateRequirement';
import {
  CHAT_MESSAGE_MAX_CHARS,
  chatMessageTooLongNotice,
} from './chatMessageLimit';
import { createSseFrameParser } from './sseFrameParser';
import {
  parseChatStreamEvent,
  type ChatStreamParseFailure,
} from './parseChatStreamEvent';
import { decideSeq } from './seqTracker';

/**
 * The server pings every 15 s while idle, so three missed pings means the
 * connection is dead even though the socket is still open. The legacy reader
 * waited 135 s for a chunk; the heartbeat lets us fail nine times faster.
 */
export const CHAT_STREAM_STALL_TIMEOUT_MS = 45_000;

/**
 * Client-side failure codes. Server codes come through the `error` event —
 * except `daily_limit_reached`, which the route refuses with a 429 before any
 * stream opens, and which shares the `error` event's code so the same notice
 * copy covers both (ORA-459).
 */
export type ChatStreamFailureCode =
  | 'unauthorized'
  | 'http_error'
  | 'network_error'
  | 'stalled'
  | 'stream_closed'
  | 'daily_limit_reached'
  /**
   * The message itself was too long for the route to accept (ORA-750). Its own
   * code because the composer answers it at the composer — the student has to
   * edit the text, which no retry button can do for them.
   */
  | 'message_too_long'
  /**
   * The backend's version gate refused this build (a 426). No retry can pass
   * it, so the turn hands off to the app's client-update prompt (ORA-1118).
   */
  | 'client_update_required';

/**
 * The allowance a daily-limit refusal reported. `chatsLimit` is null when the
 * server did not restate the cap, which means "keep the one you have" rather
 * than "uncapped".
 */
export interface ChatStreamLimitDetail {
  chatsRemaining: number;
  chatsLimit: number | null;
}

export interface ChatStreamFailure {
  code: ChatStreamFailureCode;
  message: string;
  retryable: boolean;
  /** HTTP status, when the request itself failed. */
  status?: number;
  /** The server's own error code from a non-200 JSON body, when present. */
  serverCode?: string;
  /** Present only on a daily-limit refusal, to move the meter. */
  limits?: ChatStreamLimitDetail;
}

/**
 * The v5 route's daily-limit refusals. `AI_BUDGET_EXCEEDED` and
 * `CHAT_CONCURRENCY_LIMIT` also come back as 429s and are deliberately not
 * here: neither means the student has spent their day.
 */
const DAILY_LIMIT_CODES = new Set([
  'DAILY_CHAT_RESPONSE_LIMIT',
  'DAILY_MESSAGE_LIMIT_GLOBAL',
]);

/**
 * Attachment refusals are terminal request validation, even when the route
 * uses 403: they are the server's reason, never an authentication failure.
 */
const ATTACHMENT_REFUSAL_CODES = new Set([
  'IMAGE_BLOCKED',
  'INVALID_ATTACHMENT',
  'ATTACHMENT_LOAD_FAILED',
]);

export type ChatStreamWarning =
  | {
      type: 'seq_gap';
      turnId: string;
      expectedSeq: number;
      receivedSeq: number;
    }
  | { type: 'seq_duplicate'; turnId: string; receivedSeq: number }
  /** A well-formed event this client version does not know how to render. */
  | { type: 'event_skipped'; turnId: string; seq: number; event: string }
  | { type: 'malformed_event'; event: string; reason: ChatStreamParseFailure }
  | { type: 'protocol_mismatch'; expected: number; received: number };

export interface ChatStreamHandlers {
  /** Called once per accepted event, in wire order. */
  onEvent: (event: ChatStreamEvent) => void;
  /** Non-fatal protocol observations. Never called for events that are applied normally. */
  onWarning?: (warning: ChatStreamWarning) => void;
}

export type ChatStreamStatus =
  /** `turn.done` arrived. */
  | 'completed'
  /** An `error` event arrived; it is terminal but the turn state is intact. */
  | 'error'
  /** The caller aborted. */
  | 'aborted'
  /** Transport gave out: HTTP, network, stall, or a stream that just ended. */
  | 'failed';

export interface ChatStreamOutcome {
  status: ChatStreamStatus;
  /** The server turn id, once `turn.start` (or any event) has been seen. */
  turnId: string | null;
  /** Highest seq seen, or null when the stream produced no readable event. */
  lastSeq: number | null;
  sawSeqGap: boolean;
  failure?: ChatStreamFailure;
}

export interface ChatStreamRun {
  /** Stop reading and cancel the request. Resolves `completed` to `aborted`. */
  abort: () => void;
  /** Resolves exactly once. Never rejects — every ending is an outcome. */
  completed: Promise<ChatStreamOutcome>;
}

export interface ChatStreamDeps {
  /** Backend origin, e.g. `https://api.oraai.com`. */
  baseUrl: string;
  fetchImpl: typeof fetch;
  /**
   * Called when the backend no longer accepts the session cookie (a 401), so
   * the app can sign the tab out exactly as the axios client does.
   */
  onUnauthorized?: () => void;
  /**
   * Called on a 403 `TWO_FACTOR_SETUP_REQUIRED` (an admin who has not
   * enrolled in two-factor authentication), so the app can send them to
   * enrol exactly as the axios client does (ORA-1004).
   */
  onTwoFactorSetupRequired?: () => void;
  /**
   * Called on a 426 the version gate sent with its requirement, so the app can
   * show the client-update prompt exactly as the axios client does (ORA-1118).
   */
  onClientUpdateRequired?: (requirement: ClientUpdateRequiredPayload) => void;
  /** How long without any event (including pings) before we give up. */
  stallTimeoutMs?: number;
  /** The protocol version to compare `turn.start.protocol` against. */
  protocolVersion?: number;
}

const failureFor = (
  code: ChatStreamFailureCode,
  message: string,
  extra: Partial<ChatStreamFailure> = {}
): ChatStreamFailure => ({
  code,
  message,
  retryable: code !== 'unauthorized',
  ...extra,
});

const readNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** What a 426 without the gate's own sentence tells the student. */
const CLIENT_UPDATE_REQUIRED_NOTICE =
  'A new version of Ora is required. Refresh or update Ora to keep chatting.';

/** The code ORA-743's route uses for a message over the length cap. */
const MESSAGE_TOO_LONG_CODE = 'MESSAGE_TOO_LONG';

/** Does a validation detail's `path` point at the turn's `message` field? */
const isMessagePath = (path: unknown): boolean =>
  Array.isArray(path)
    ? path.some((segment) => segment === 'message')
    : path === 'message';

/**
 * Is this 400 a refusal of the message's length?
 *
 * Two shapes are accepted on purpose, because the backend may ship after this
 * client: the explicit `MESSAGE_TOO_LONG` code, and the older generic
 * `INVALID_TURN_REQUEST` whose validation `details` name the `message` field and
 * the cap. The second is recognised by reading the details it actually has —
 * a detail whose `path` is the `message` field, complaining about the cap —
 * rather than by looking for the two words anywhere in the serialized body,
 * which would have matched a refusal of a document named "12000 messages".
 */
const isMessageTooLongBody = (
  status: number,
  payload: { code?: string; details?: unknown }
): boolean => {
  if (status !== 400) return false;
  if (payload.code === MESSAGE_TOO_LONG_CODE) return true;
  if (!Array.isArray(payload.details)) return false;

  return payload.details.some((detail) => {
    if (typeof detail !== 'object' || detail === null) return false;

    const row = detail as { path?: unknown; message?: unknown };

    return (
      isMessagePath(row.path) &&
      typeof row.message === 'string' &&
      row.message.includes(String(CHAT_MESSAGE_MAX_CHARS))
    );
  });
};

/**
 * Read a non-200 JSON body. The daily-limit refusal carries the allowance in
 * the snake-case shape the v5 route sends (`daily_limit`,
 * `responses_remaining`); anything missing falls back to "nothing left", which
 * is the only thing a limit refusal can mean.
 */
const readErrorBody = async (
  response: Response
): Promise<{
  message?: string;
  code?: string;
  limits?: ChatStreamLimitDetail;
  /** Set when the refusal is about the message length (ORA-750). */
  messageTooLong?: { limit: number };
  /** The version gate's requirement, on a 426 that carried one (ORA-1118). */
  clientUpdate?: ClientUpdateRequiredPayload;
}> => {
  try {
    const payload = (await response.json()) as {
      error?: string;
      message?: string;
      code?: string;
      details?: unknown;
      limit?: unknown;
      daily_limit?: unknown;
      responses_remaining?: unknown;
    };

    const isDailyLimit =
      response.status === 429 &&
      typeof payload.code === 'string' &&
      DAILY_LIMIT_CODES.has(payload.code);

    const clientUpdate =
      response.status === 426
        ? parseClientUpdateRequiredPayload(payload)
        : null;

    return {
      message: payload.error ?? payload.message,
      code: payload.code,
      ...(clientUpdate ? { clientUpdate } : {}),
      ...(isMessageTooLongBody(response.status, payload)
        ? {
            messageTooLong: {
              limit: readNumber(payload.limit) ?? CHAT_MESSAGE_MAX_CHARS,
            },
          }
        : {}),
      ...(isDailyLimit
        ? {
            limits: {
              chatsRemaining: Math.max(
                0,
                readNumber(payload.responses_remaining) ?? 0
              ),
              chatsLimit: readNumber(payload.daily_limit),
            },
          }
        : {}),
    };
  } catch {
    return {};
  }
};

/**
 * Open a chat turn on the v5 route and stream ora-be ADR-0006 events from it.
 *
 * The seam is deliberately small: a request in, typed events out, exactly one
 * outcome. Ordering (duplicate and gap handling) and the heartbeat stall are
 * enforced here so every consumer gets the same guarantees; what the events
 * *mean* is the chat slice's job.
 *
 * Auth is the Better Auth session cookie (ORA-987), sent with
 * `credentials: 'include'`. There is nothing to refresh: a 401 means the
 * session is gone, so the turn fails as unauthorized and `onUnauthorized`
 * signs the tab out.
 */
export const runChatTurnStream = (
  request: ChatTurnRequest,
  handlers: ChatStreamHandlers,
  deps: ChatStreamDeps
): ChatStreamRun => {
  const controller = new AbortController();
  const stallTimeoutMs = deps.stallTimeoutMs ?? CHAT_STREAM_STALL_TIMEOUT_MS;
  const expectedProtocol = deps.protocolVersion ?? 1;
  let aborted = false;
  let stalled = false;
  let cancelActiveReader: (() => void) | null = null;
  const warn = (warning: ChatStreamWarning) => handlers.onWarning?.(warning);

  const send = async () =>
    deps.fetchImpl(`${deps.baseUrl}${CHAT_TURN_ROUTE}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        // The platform header is the backend's CSRF fallback for a cookie
        // request without Origin; the web build must ride with it, or the
        // version gate refuses the turn (426) as a client it cannot date.
        ...(await getClientMetadataHeaders()),
      },
      body: JSON.stringify({
        ...request,
        ...(request.attachments
          ? {
              attachments: request.attachments.map(
                ({ key, mimeType, sizeBytes }) => ({ key, mimeType, sizeBytes })
              ),
            }
          : {}),
      }),
      signal: controller.signal,
    });

  const run = async (): Promise<ChatStreamOutcome> => {
    let turnId: string | null = null;
    let lastSeq: number | null = null;
    let sawSeqGap = false;

    const outcome = (
      status: ChatStreamStatus,
      failure?: ChatStreamFailure
    ): ChatStreamOutcome => ({
      status,
      turnId,
      lastSeq,
      sawSeqGap,
      ...(failure ? { failure } : {}),
    });

    let response: Response;

    try {
      response = await send();
    } catch (error) {
      if (aborted) return outcome('aborted');

      return outcome(
        'failed',
        failureFor(
          'network_error',
          error instanceof Error ? error.message : 'Chat turn request failed'
        )
      );
    }

    if (!response.ok) {
      const body = await readErrorBody(response);

      /**
       * The version gate refused this build. Retrying sends the same build and
       * is refused the same way (Oct 21: one student retried 21 times), so the
       * turn fails for good and the app's update prompt takes over, as it
       * does for an axios 426 (ORA-1118).
       */
      if (response.status === 426) {
        if (body.clientUpdate) deps.onClientUpdateRequired?.(body.clientUpdate);

        return outcome(
          'failed',
          failureFor(
            'client_update_required',
            body.clientUpdate?.message ?? CLIENT_UPDATE_REQUIRED_NOTICE,
            {
              status: response.status,
              ...(body.code ? { serverCode: body.code } : {}),
              retryable: false,
            }
          )
        );
      }

      const isAttachmentRefusal =
        body.code !== undefined && ATTACHMENT_REFUSAL_CODES.has(body.code);

      const unauthorized =
        (response.status === 401 || response.status === 403) &&
        !isAttachmentRefusal;

      // The session cookie is gone: sign the tab out, as axios would.
      if (response.status === 401) deps.onUnauthorized?.();

      // An admin without two-factor authentication: send them to enrol, as
      // axios would, rather than leave them with "sign in again".
      if (
        response.status === 403 &&
        body.code === TWO_FACTOR_SETUP_REQUIRED_CODE
      ) {
        deps.onTwoFactorSetupRequired?.();
      }

      // A spent daily allowance is not a transport hiccup: retrying would be
      // refused the same way, so it is reported as the limit it is and carries
      // the numbers the meter needs (ORA-459).
      const dailyLimit = body.limits !== undefined;
      /**
       * A message over the length cap is the student's text, not the transport
       * (ORA-750): the composer answers it inline and hands the draft back, so
       * it gets its own code rather than the generic `http_error` a "something
       * went wrong, retry?" notice is drawn from.
       */
      const tooLong = body.messageTooLong;

      const code: ChatStreamFailureCode = tooLong
        ? 'message_too_long'
        : dailyLimit
          ? 'daily_limit_reached'
          : unauthorized
            ? 'unauthorized'
            : 'http_error';

      /**
       * The server's own sentence is shown only when it sent the code that comes
       * with one. The older refusal's prose is "Invalid turn request", which
       * tells a student nothing — for that one the client writes the sentence
       * itself, out of the length it just tried to send.
       */
      const message = tooLong
        ? ((body.code === MESSAGE_TOO_LONG_CODE ? body.message : undefined) ??
          chatMessageTooLongNotice(request.message.length, tooLong.limit))
        : (body.message ?? `Chat turn failed with status ${response.status}`);

      return outcome(
        'failed',
        failureFor(code, message, {
          status: response.status,
          ...(body.code ? { serverCode: body.code } : {}),
          ...(body.limits ? { limits: body.limits } : {}),
          retryable:
            !unauthorized &&
            !dailyLimit &&
            response.status !== 400 &&
            !isAttachmentRefusal,
        })
      );
    }

    if (!response.body) {
      return outcome(
        'failed',
        failureFor('network_error', 'Chat turn response had no body')
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const frames = createSseFrameParser();
    let terminal: ChatStreamStatus | null = null;

    const cancelReader = () => {
      void reader.cancel().catch(() => {
        // The stream is going away either way.
      });
    };

    cancelActiveReader = cancelReader;

    // A stall is "no event at all for stallTimeoutMs" — pings reset it, so a
    // long tool round is fine and a dead connection is not.
    const readWithStallTimeout = () =>
      new Promise<ReadableStreamReadResult<Uint8Array>>((resolve, reject) => {
        const timer = setTimeout(() => {
          stalled = true;
          cancelReader();
          reject(new Error(`Chat stream stalled for ${stallTimeoutMs}ms`));
        }, stallTimeoutMs);

        reader
          .read()
          .then(resolve, reject)
          .finally(() => clearTimeout(timer));
      });

    /**
     * Advance the seq cursor for one envelope. Returns false when the event
     * must be ignored (a repeat or an out-of-order delivery).
     */
    const advanceSeq = (eventTurnId: string, seq: number): boolean => {
      const decision = decideSeq(lastSeq, seq);

      if (decision === 'duplicate') {
        warn({ type: 'seq_duplicate', turnId: eventTurnId, receivedSeq: seq });

        return false;
      }

      if (decision === 'gap' && lastSeq !== null) {
        sawSeqGap = true;

        warn({
          type: 'seq_gap',
          turnId: eventTurnId,
          expectedSeq: lastSeq + 1,
          receivedSeq: seq,
        });
      }

      lastSeq = seq;

      return true;
    };

    const consume = (frameList: ReturnType<typeof frames.push>) => {
      for (const frame of frameList) {
        if (terminal) break;

        const parsed = parseChatStreamEvent(frame);

        if (!parsed.ok) {
          // An unknown event name means a newer server, not a hole in the
          // stream, so skip it but keep the cursor moving — otherwise the next
          // event reads as a gap. A frame we could not even read an envelope
          // from is a genuine loss and is left to trip the gap check.
          if (parsed.reason === 'unknown_event' && parsed.envelope) {
            const { turnId: skippedTurnId, seq } = parsed.envelope;

            if (turnId === null) turnId = skippedTurnId;

            if (advanceSeq(skippedTurnId, seq)) {
              warn({
                type: 'event_skipped',
                turnId: skippedTurnId,
                seq,
                event: frame.event,
              });
            }

            continue;
          }

          warn({
            type: 'malformed_event',
            event: frame.event,
            reason: parsed.reason,
          });

          continue;
        }

        const event = parsed.event;

        if (turnId === null) turnId = event.turnId;
        if (!advanceSeq(event.turnId, event.seq)) continue;

        if (
          event.type === 'turn.start' &&
          event.protocol !== expectedProtocol
        ) {
          warn({
            type: 'protocol_mismatch',
            expected: expectedProtocol,
            received: event.protocol,
          });
        }

        handlers.onEvent(event);

        if (isTerminalChatStreamEvent(event)) {
          terminal = event.type === 'turn.done' ? 'completed' : 'error';
        }
      }
    };

    try {
      while (!terminal) {
        if (aborted) break;

        const { done, value } = await readWithStallTimeout();
        if (done) {
          consume(frames.flush());
          break;
        }

        consume(frames.push(decoder.decode(value, { stream: true })));
      }
    } catch (error) {
      if (aborted) return outcome('aborted');

      if (stalled) {
        return outcome(
          'failed',
          failureFor(
            'stalled',
            error instanceof Error ? error.message : 'Chat stream stalled'
          )
        );
      }

      return outcome(
        'failed',
        failureFor(
          'network_error',
          error instanceof Error ? error.message : 'Chat stream failed'
        )
      );
    } finally {
      cancelReader();
      cancelActiveReader = null;
    }

    if (aborted) return outcome('aborted');
    if (terminal) return outcome(terminal);

    // The connection closed without `turn.done` or `error`.
    return outcome(
      'failed',
      failureFor('stream_closed', 'Chat stream ended without a terminal event')
    );
  };

  return {
    abort: () => {
      aborted = true;
      controller.abort();
      cancelActiveReader?.();
    },
    completed: run(),
  };
};
